import { Adjustment } from 'components/order-details/adjust-order-drawer';
import { TransactionGroup } from 'components/order-details/transactions';
import { Order } from 'components/pages/order-details';
import { convertToPennies } from 'core/utils';
import {
  Confirmation,
  PendingPayment,
  PreviewLineItems,
} from 'hooks/postRegEditDrawer';
import { Dispatch } from 'react';
import { OrderDetailsAction } from './reducer';
import {
  cancelFees,
  createOfflinePayment,
  getInstallments,
  getNotes,
  getOrder,
  getPreviewLineItems,
  getTransactions,
  refundTransaction,
  updateInstallment,
  updateLineItems,
} from './service';
import { ActionType, Installment, LoadingType, Note, Refund } from './types';

export interface OrderDetailsActions {
  setOrgId: (orgId: number) => void;
  getOrder: (orderNumber: string) => Promise<void>;
  getNotes: (orderId: number) => Promise<void>;
  cancelFees: (
    orderId: number,
    orderLineItemIds: number[],
    deleteRosterProfiles: boolean
  ) => Promise<void>;
  getTransactions: (orderId: number) => Promise<void>;
  refundTransaction: (
    orderId: number,
    paymentId: number,
    amount: number,
    note: string,
    type: string
  ) => Promise<Refund>;
  getInstallments: (orderId: number) => Promise<void>;
  updateInstallment: (
    orderId: number,
    canceled: boolean,
    scheduledDate?: string
  ) => Promise<{ id: number }>;
  createOfflinePayment: (
    orderId: number,
    amount: number,
    offlinePaymentMethodType: string,
    note: string
  ) => Promise<{ id: number }>;
  getPreviewLineItems: (
    orderId: number,
    adjustment: Adjustment
  ) => Promise<void>;
  clearPreviewLineItems: () => void;
  saveOrderAdjustment: (
    orderId: number,
    adjustment: Adjustment,
    pendingPayment: PendingPayment
  ) => Promise<Confirmation & { installments: Installment[] }>;
}

export const getActions = (
  dispatch: Dispatch<OrderDetailsAction>
): OrderDetailsActions => ({
  setOrgId: (orgId: number) => {
    dispatch({
      type: ActionType.SetOrgId,
      payload: { orgId },
    });
  },
  getOrder: async (orderNumber: string) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetOrder, isLoading: true },
    });
    try {
      const { data: order } = await getOrder<{ showOrder: Order }>(orderNumber);
      dispatch({
        type: ActionType.SetOrder,
        payload: { order: order.showOrder },
      });
    } catch (e) {
      dispatch({
        type: ActionType.SetError,
        payload: { error: e.message },
      });
    }
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetOrder, isLoading: false },
    });
  },
  getNotes: async (orderId: number) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetNotes, isLoading: true },
    });
    try {
      const { data: notes } = await getNotes<Note[]>(orderId);
      dispatch({ type: ActionType.SetNotes, payload: { notes } });
    } catch (e) {
      dispatch({
        type: ActionType.SetError,
        payload: { error: e.message },
      });
    }

    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetNotes, isLoading: false },
    });
  },
  cancelFees: async (orderId, orderLineItemIds, deleteRosterProfiles) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.CancelFees, isLoading: true },
    });
    try {
      await cancelFees(orderId, orderLineItemIds, deleteRosterProfiles);
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    }
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.CancelFees, isLoading: false },
    });
  },
  getTransactions: async (orderId) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetTransactions, isLoading: true },
    });
    try {
      const { data: transactions } = await getTransactions<{
        listTransactionGroups: TransactionGroup[];
      }>(orderId);
      dispatch({
        type: ActionType.SetTransactions,
        payload: { transactions: transactions.listTransactionGroups },
      });
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.GetTransactions,
          isLoading: false,
        },
      });
    }
  },
  refundTransaction: async (orderId, paymentId, amount, note, type) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.RefundTransaction, isLoading: true },
    });
    try {
      const { data: response } = await refundTransaction<{
        createRefund: Refund;
      }>(orderId, paymentId, amount, note, type);
      return response?.createRefund;
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.RefundTransaction,
          isLoading: false,
        },
      });
    }
  },
  getInstallments: async (orderId) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.GetInstallments, isLoading: true },
    });
    try {
      const { data: installments } = await getInstallments<{
        installments: Installment[];
      }>(orderId);
      dispatch({
        type: ActionType.SetInstallments,
        payload: { installments: installments.installments },
      });
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.GetInstallments,
          isLoading: false,
        },
      });
    }
  },
  updateInstallment: async (id, canceled, scheduledDate) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.UpdateInstallment, isLoading: true },
    });
    try {
      const { data: response } = await updateInstallment<{
        updateInstallment: { id: number };
      }>(id, canceled, scheduledDate);
      return response?.updateInstallment;
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.UpdateInstallment,
          isLoading: false,
        },
      });
    }
  },
  createOfflinePayment: async (
    orderId,
    amount,
    offlinePaymentMethodType,
    note
  ) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: {
        loadingType: LoadingType.CreateOfflinePayment,
        isLoading: true,
      },
    });
    try {
      const { data: response } = await createOfflinePayment<{
        applyOfflinePayment: { id: number };
      }>(orderId, amount, offlinePaymentMethodType, note);
      return response?.applyOfflinePayment;
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.CreateOfflinePayment,
          isLoading: false,
        },
      });
    }
  },
  getPreviewLineItems: async (orderId, adjustment) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: {
        loadingType: LoadingType.GetPreviewLineItems,
        isLoading: true,
      },
    });
    try {
      let adjustmentAmount = parseInt(
        String(convertToPennies(adjustment.amount))
      );
      if (adjustment.type === 'reduce') {
        adjustmentAmount *= -1;
      }

      const newLineItem = {
        amount: adjustmentAmount,
        name: 'Adjustment',
        description: adjustment.note,
        type: 'adjustment',
        canceled: false,
      } as any;
      const { data: response } = await getPreviewLineItems<PreviewLineItems>(
        orderId,
        [],
        adjustment.type === 'payment_schedule' ? [] : [newLineItem]
      );
      dispatch({
        type: ActionType.SetPreviewLineItems,
        payload: { previewLineItems: response },
      });
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.GetPreviewLineItems,
          isLoading: false,
        },
      });
    }
  },
  clearPreviewLineItems: () => {
    dispatch({
      type: ActionType.SetPreviewLineItems,
      payload: { previewLineItems: null },
    });
  },
  saveOrderAdjustment: async (orderId, adjustment, pendingPayment) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.SaveAdjustment, isLoading: true },
    });
    try {
      let adjustmentAmount: number;
      if (adjustment.type !== 'payment_schedule') {
        adjustmentAmount = parseInt(
          String(convertToPennies(adjustment.amount))
        );
        if (adjustment.type === 'reduce') {
          adjustmentAmount *= -1;
        }
      }

      const newLineItem = {
        amount: adjustmentAmount,
        name: adjustment.note || 'Adjustment',
        type: 'adjustment',
        canceled: false,
      } as any;
      const { data: response } = await updateLineItems<
        Confirmation & { installments: Installment[] }
      >(
        orderId,
        [],
        adjustment.type === 'payment_schedule' ? [] : [newLineItem],
        pendingPayment.installments,
        adjustment.note
      );
      return response;
    } catch (e) {
      dispatch({ type: ActionType.SetError, payload: { error: e.message } });
    } finally {
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType: LoadingType.SaveAdjustment, isLoading: false },
      });
    }
  },
});
